export const NEUTRAL_RING_COLOR = '#d8d8e2'

export type NpsVariant = 'success' | 'warning' | 'danger'

export type NpsClassificationLabel = 'Promotor' | 'Neutro' | 'Detrator'

export interface NpsScoreClassification {
  label: NpsClassificationLabel
  color: string
  backgroundColor: string
  variant: NpsVariant
}

/**
 * Paleta canônica de cores NPS (texto + fundo) por faixa.
 * Única fonte de verdade — NPS_STATUS_COLORS (constants/colors.ts) deriva daqui.
 */
export const NPS_PALETTE = {
  red: { color: '#DA1E28', backgroundColor: '#FCE9EA' },
  orange: { color: '#F18F1B', backgroundColor: '#FEF3E7' },
  yellow: { color: '#F1C21B', backgroundColor: '#FEF9E7' },
  green: { color: '#198038', backgroundColor: '#EAFBEF' }
} as const

/**
 * NPS "geral" de um médico/unidade: média aritmética dos NPS por avaliador
 * (Paciente, Corpo Clínico...), ignorando avaliadores sem base (null).
 *
 * Não é média das notas 0–10: cada entrada já é um NPS (-100 a 100) calculado
 * pelo backend com a regra promotores% − detratores%. Avaliador sem resposta
 * chega como null e fica fora do denominador; contá-lo como 0 puxava o número
 * para baixo (ex.: { paciente: 20, clinico: null } virava 10 em vez de 20).
 */
export function getOverallNpsScore(
  scores: Record<string, number | null | undefined>
): number | null {
  const values = Object.values(scores).filter((v): v is number => typeof v === 'number')
  if (!values.length) return null
  return Math.round(values.reduce((a, b) => a + b, 0) / values.length)
}

/**
 * Escada canônica do score NPS agregado (escala -100 a 100):
 * ≤0 zona crítica (vermelho), ≤50 Detrator (laranja), ≤75 Neutro (amarelo),
 * >75 Promotor (verde).
 */
export function classifyNpsScore(score: number): NpsScoreClassification {
  if (score <= 0) return { label: 'Detrator', variant: 'danger', ...NPS_PALETTE.red }
  if (score <= 50) return { label: 'Detrator', variant: 'danger', ...NPS_PALETTE.orange }
  if (score <= 75) return { label: 'Neutro', variant: 'warning', ...NPS_PALETTE.yellow }
  return { label: 'Promotor', variant: 'success', ...NPS_PALETTE.green }
}

/**
 * Escada canônica da nota individual (escala 0–10, padrão NPS):
 * 0–6 Detrator, 7–8 Neutro, 9–10 Promotor.
 */
export function classifyScale11Score(score: number): NpsScoreClassification {
  if (score <= 6) return { label: 'Detrator', variant: 'danger', ...NPS_PALETTE.red }
  if (score <= 8) return { label: 'Neutro', variant: 'warning', ...NPS_PALETTE.yellow }
  return { label: 'Promotor', variant: 'success', ...NPS_PALETTE.green }
}

export interface NpsGroupCounts {
  detractors: number
  neutrals: number
  promoters: number
}

export interface NpsGroupDistributionItem {
  key: keyof NpsGroupCounts
  /** Rótulo plural exibido na legenda (ex.: "Detratores"). */
  label: string
  /** Faixa de notas que define o grupo (ex.: "0–6"). */
  range: string
  count: number
  /** Percentual inteiro; a soma dos três é sempre 100 quando há respostas, ou 0 quando não há. */
  percent: number
  color: string
  backgroundColor: string
  variant: NpsVariant
}

/**
 * Distribuição Detratores / Neutros / Promotores a partir das contagens do
 * backend, que já classifica pelas faixas canônicas da escala 0–10
 * (0–6, 7–8, 9–10 — as mesmas de `classifyScale11Score`).
 *
 * Percentuais são arredondados pelo método dos maiores restos, para que a soma
 * feche exatamente em 100 mesmo quando o arredondamento simples daria 99 ou 101
 * (ex.: 1/3, 1/3, 1/3 → 34, 33, 33). Sem respostas, todos ficam em 0.
 */
export function getNpsGroupDistribution(counts: NpsGroupCounts): NpsGroupDistributionItem[] {
  const groups: Omit<NpsGroupDistributionItem, 'percent'>[] = [
    {
      key: 'detractors',
      label: 'Detratores',
      range: '0–6',
      count: counts.detractors,
      variant: 'danger',
      ...NPS_PALETTE.red
    },
    {
      key: 'neutrals',
      label: 'Neutros',
      range: '7–8',
      count: counts.neutrals,
      variant: 'warning',
      ...NPS_PALETTE.yellow
    },
    {
      key: 'promoters',
      label: 'Promotores',
      range: '9–10',
      count: counts.promoters,
      variant: 'success',
      ...NPS_PALETTE.green
    }
  ]

  const total = groups.reduce((sum, group) => sum + group.count, 0)
  if (total <= 0) return groups.map((group) => ({ ...group, percent: 0 }))

  const exact = groups.map((group) => (group.count / total) * 100)
  const floored = exact.map(Math.floor)
  let remainder = 100 - floored.reduce((sum, value) => sum + value, 0)

  const byLargestRemainder = exact
    .map((value, index) => ({ index, rest: value - floored[index] }))
    .sort((a, b) => b.rest - a.rest || a.index - b.index)

  for (const { index } of byLargestRemainder) {
    if (remainder <= 0) break
    floored[index] += 1
    remainder -= 1
  }

  return groups.map((group, index) => ({ ...group, percent: floored[index] }))
}

/** Cores vivas da barra de zonas do NpsScoreSelector. */
export const NPS_ZONE_COLORS = { orange: '#fc6736', yellow: '#fcea2b', green: '#64ff70' } as const

const VARIANT_TO_ZONE_COLOR: Record<NpsVariant, string> = {
  danger: NPS_ZONE_COLORS.orange,
  warning: NPS_ZONE_COLORS.yellow,
  success: NPS_ZONE_COLORS.green
}

export function getNpsZoneColor(score: number): string {
  return VARIANT_TO_ZONE_COLOR[classifyScale11Score(score).variant]
}
